import { reactive, provide, inject } from 'vue'

// 类型定义
export interface AddressOption {
  id: number
  name: string
  code: string
  nameCn?: string
  nameEn?: string
}

export interface CompleteAddressInfo {
  instanceId: string
  level1: AddressOption | null
  level2: AddressOption | null
  level3: AddressOption | null
  level4: AddressOption | null
  level5: AddressOption | null
  level6: AddressOption | null
  formattedAddress: string
  rawData: {
    level1Id?: number
    level1Name?: string
    level2Id?: number
    level2Name?: string
    level3Id?: number
    level3Name?: string
    level4Id?: number
    level4Name?: string
    level5Id?: number
    level5Name?: string
    level6Id?: number
    level6Name?: string
  }
}

// 全局事件总线
export const eventBus = {
  listeners: {} as Record<string, Function[]>,
  on(event: string, callback: Function) {
    if (!this.listeners[event]) {
      this.listeners[event] = []
    }
    this.listeners[event].push(callback)
  },
  off(event: string, callback: Function) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback)
    }
  },
  emit(event: string, data: any) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => callback(data))
    }
  }
}

// 地址状态管理器类型定义
export interface AddressStateManager {
  addressState: any
  getOrCreateInstance: (instanceId: string) => any
  getInstance: (instanceId: string) => any
  updateSelection: (instanceId: string, level: number, selection: AddressOption | null) => void
  setSelectionDirectly: (instanceId: string, level: number, selection: AddressOption | null) => void
  clearLowerLevels: (instanceId: string, fromLevel: number) => void
  clearLowerLevelCache: (instanceId: string, fromLevel: number) => void
  triggerLowerLevelQueries: (instanceId: string, fromLevel: number) => void
  getParentAddressId: (instanceId: string, currentLevel: number) => number | null
  getCompleteAddress: (instanceId: string) => CompleteAddressInfo | null
  formatAddress: (address: any) => string
  initializeFromApiData: (regionItems: any[]) => void
  initializeFromApiDataWithInstanceId: (instanceId: string, regionData: any) => void
  eventBus: typeof eventBus
}

// 地址状态管理
export const useAddressState = (): AddressStateManager => {
  const addressState = reactive({
    instances: {} as Record<string, {
      selections: Record<number, AddressOption | null>
      optionsCache: Record<number, Record<number, AddressOption[]>>
      loadingStates: Record<number, boolean>
      lastUpdated: Record<number, number>
    }>
  })

  // 获取或创建实例
  const getOrCreateInstance = (instanceId: string) => {
    if (!addressState.instances[instanceId]) {
      addressState.instances[instanceId] = {
        selections: {},
        optionsCache: {},
        loadingStates: {},
        lastUpdated: {}
      }
    }
    return addressState.instances[instanceId]
  }

  // 获取实例
  const getInstance = (instanceId: string) => {
    return addressState.instances[instanceId]
  }

  // 更新地址选择
  const updateSelection = (instanceId: string, level: number, selection: AddressOption | null) => {
    const instance = getOrCreateInstance(instanceId)
    instance.selections[level] = selection

    // 清空下级所有选择
    clearLowerLevels(instanceId, level + 1)

    // 清空下级缓存
    clearLowerLevelCache(instanceId, level + 1)

    // 触发下级自动查询
    triggerLowerLevelQueries(instanceId, level + 1)
  }

  // 清空下级选择
  const clearLowerLevels = (instanceId: string, fromLevel: number) => {
    const instance = getInstance(instanceId)
    if (!instance) return

    for (let level = fromLevel; level <= 6; level++) {
      instance.selections[level] = null
    }
  }

  // 清空下级缓存
  const clearLowerLevelCache = (instanceId: string, fromLevel: number) => {
    const instance = getInstance(instanceId)
    if (!instance) return

    for (let level = fromLevel; level <= 6; level++) {
      instance.optionsCache[level] = {}
    }
  }

  // 触发下级自动查询
  const triggerLowerLevelQueries = (instanceId: string, fromLevel: number) => {
    console.log(`触发下级自动查询: ${instanceId} fromLevel: ${fromLevel}`)
    for (let level = fromLevel; level <= 6; level++) {
      const parentId = getParentAddressId(instanceId, level)
      console.log(`检查 level ${level}, parentId: ${parentId}`)
      if (parentId) {
        // 通过事件总线通知对应组件进行自动查询
        console.log(`发送自动查询事件: ${instanceId} level${level} parentId: ${parentId}`)
        eventBus.emit('address-auto-query', {
          instanceId,
          level,
          parentId
        })
      }
    }
  }

  // 获取上级地址ID
  const getParentAddressId = (instanceId: string, currentLevel: number): number | null => {
    const instance = getInstance(instanceId)
    if (!instance) return null

    const parentLevel = currentLevel - 1
    const parentSelection = instance.selections[parentLevel]

    return parentSelection?.id || null
  }

  // 获取完整地址信息
  const getCompleteAddress = (instanceId: string): CompleteAddressInfo | null => {
    const instance = getInstance(instanceId)
    if (!instance) return null

    const completeAddress = {
      level1: instance.selections[1] || null,
      level2: instance.selections[2] || null,
      level3: instance.selections[3] || null,
      level4: instance.selections[4] || null,
      level5: instance.selections[5] || null,
      level6: instance.selections[6] || null
    }

    return {
      instanceId,
      ...completeAddress,
      formattedAddress: formatAddress(completeAddress),
      rawData: {
        level1Id: completeAddress.level1?.id,
        level1Name: completeAddress.level1?.name,
        level2Id: completeAddress.level2?.id,
        level2Name: completeAddress.level2?.name,
        level3Id: completeAddress.level3?.id,
        level3Name: completeAddress.level3?.name,
        level4Id: completeAddress.level4?.id,
        level4Name: completeAddress.level4?.name,
        level5Id: completeAddress.level5?.id,
        level5Name: completeAddress.level5?.name,
        level6Id: completeAddress.level6?.id,
        level6Name: completeAddress.level6?.name
      }
    }
  }

  // 格式化地址
  const formatAddress = (address: {
    level1: AddressOption | null
    level2: AddressOption | null
    level3: AddressOption | null
    level4: AddressOption | null
    level5: AddressOption | null
    level6: AddressOption | null
  }): string => {
    const parts = [
      address.level1?.name,
      address.level2?.name,
      address.level3?.name,
      address.level4?.name,
      address.level5?.name,
      address.level6?.name
    ].filter(Boolean)

    return parts.join(' ')
  }

  // 直接设置选择状态（不触发级联清空，用于初始化）
  const setSelectionDirectly = (instanceId: string, level: number, selection: AddressOption | null) => {
    const instance = getOrCreateInstance(instanceId)
    instance.selections[level] = selection
  }

  // 从API数据初始化状态
  const initializeFromApiData = (regionItems: any[]) => {
    regionItems.forEach((region, index) => {
      const instanceId = `region-${index}`

      // 直接设置各级选择状态，不触发级联清空
      if (region.level1Id) {
        setSelectionDirectly(instanceId, 1, {
          id: Number(region.level1Id),
          name: region.level1Name,
          code: region.code || ''
        })
      }

      if (region.level2Id) {
        setSelectionDirectly(instanceId, 2, {
          id: Number(region.level2Id),
          name: region.level2Name,
          code: region.code || ''
        })
      }

      if (region.level3Id) {
        setSelectionDirectly(instanceId, 3, {
          id: Number(region.level3Id),
          name: region.level3Name,
          code: region.code || ''
        })
      }

      if (region.level4Id) {
        setSelectionDirectly(instanceId, 4, {
          id: Number(region.level4Id),
          name: region.level4Name,
          code: region.code || ''
        })
      }

      if (region.level5Id) {
        setSelectionDirectly(instanceId, 5, {
          id: Number(region.level5Id),
          name: region.level5Name,
          code: region.code || ''
        })
      }

      if (region.level6Id) {
        setSelectionDirectly(instanceId, 6, {
          id: Number(region.level6Id),
          name: region.level6Name,
          code: region.code || ''
        })
      }
    })
  }

  // 使用指定的instanceId从API数据初始化单个地址实例
  // 适用于需要自定义instanceId的场景，如装箱门点地址(stuffing-region-0)
  const initializeFromApiDataWithInstanceId = (instanceId: string, regionData: any) => {
    // 先清空所有层级，避免二次打开时残留上次的旧值
    const instance = getOrCreateInstance(instanceId)
    for (let level = 1; level <= 6; level++) {
      instance.selections[level] = null
    }

    // 直接设置各级选择状态，不触发级联清空
    if (regionData.level1Id) {
      setSelectionDirectly(instanceId, 1, {
        id: Number(regionData.level1Id),
        name: regionData.level1Name,
        code: regionData.code || ''
      })
    }

    if (regionData.level2Id) {
      setSelectionDirectly(instanceId, 2, {
        id: Number(regionData.level2Id),
        name: regionData.level2Name,
        code: regionData.code || ''
      })
    }

    if (regionData.level3Id) {
      setSelectionDirectly(instanceId, 3, {
        id: Number(regionData.level3Id),
        name: regionData.level3Name,
        code: regionData.code || ''
      })
    }

    if (regionData.level4Id) {
      setSelectionDirectly(instanceId, 4, {
        id: Number(regionData.level4Id),
        name: regionData.level4Name,
        code: regionData.code || ''
      })
    }

    if (regionData.level5Id) {
      setSelectionDirectly(instanceId, 5, {
        id: Number(regionData.level5Id),
        name: regionData.level5Name,
        code: regionData.code || ''
      })
    }

    if (regionData.level6Id) {
      setSelectionDirectly(instanceId, 6, {
        id: Number(regionData.level6Id),
        name: regionData.level6Name,
        code: regionData.code || ''
      })
    }
  }

  return {
    addressState,
    getOrCreateInstance,
    getInstance,
    updateSelection,
    setSelectionDirectly,
    clearLowerLevels,
    clearLowerLevelCache,
    triggerLowerLevelQueries,
    getParentAddressId,
    getCompleteAddress,
    formatAddress,
    initializeFromApiData,
    initializeFromApiDataWithInstanceId,
    eventBus
  }
}

// Provide/Inject 键
const ADDRESS_STATE_KEY = Symbol('address-state')

// 提供地址状态
export const provideAddressState = () => {
  const addressStateManager = useAddressState()
  provide(ADDRESS_STATE_KEY, addressStateManager)
  return addressStateManager
}

// 注入地址状态
export const injectAddressState = (): AddressStateManager => {
  const addressStateManager = inject<AddressStateManager>(ADDRESS_STATE_KEY)
  if (!addressStateManager) {
    throw new Error('useAddressState must be used within a component that provides address state')
  }
  return addressStateManager
}
